import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Users, Briefcase, Heart, Star, MapPin, Loader2, Sparkles, Trophy, GraduationCap, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PeopleLikeYouPage = ({ user }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myCareers, setMyCareers] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);

  useEffect(() => { fetchMatches(); }, []);

  const fetchMatches = async () => {
    try {
      const res = await fetch(`${API}/api/ai/people-like-you`, { headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        setMatches(data.matches || []);
        setMyCareers(data.your_careers || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const getMatchColor = (score) => {
    if (score >= 80) return { bg: 'from-emerald-500 to-teal-500', text: 'text-emerald-700', light: 'bg-emerald-50' };
    if (score >= 60) return { bg: 'from-blue-500 to-indigo-500', text: 'text-blue-700', light: 'bg-blue-50' };
    if (score >= 40) return { bg: 'from-violet-500 to-purple-500', text: 'text-violet-700', light: 'bg-violet-50' };
    return { bg: 'from-amber-500 to-orange-500', text: 'text-amber-700', light: 'bg-amber-50' };
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3" style={{ backgroundColor: '#F5F3EF' }}>
      <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      <p className="text-sm text-gray-500">Finding people with similar career interests...</p>
    </div>
  );

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)' }}>
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -mr-10 -mt-10" />
        <div className="relative p-6 pb-8">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30"><ArrowLeft className="w-5 h-5 text-white" /></button>
            <div>
              <h1 className="text-xl font-bold text-white">People Like You</h1>
              <p className="text-white/70 text-sm">Youth who share your career interests</p>
            </div>
          </div>
          {myCareers.length > 0 ? (
            <div className="bg-white/15 backdrop-blur rounded-2xl p-3">
              <p className="text-white/80 text-xs font-medium mb-2">Your Career Interests</p>
              <div className="flex flex-wrap gap-1.5">
                {myCareers.map((c, i) => (
                  <Badge key={i} className="bg-white/25 text-white text-xs border-0">{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Badge>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white/15 backdrop-blur rounded-2xl p-4 text-center">
              <p className="text-white/90 text-sm mb-2">Set your career interests to find matches!</p>
              <Button onClick={() => navigate('/profile')} size="sm" className="bg-white text-violet-600 hover:bg-white/90 rounded-xl text-xs">Update Profile</Button>
            </div>
          )}
        </div>
      </div>

      {/* Matches */}
      <div className="max-w-lg mx-auto px-4 py-5 space-y-3">
        {matches.length === 0 ? (
          <Card className="p-8 text-center rounded-2xl">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-bold text-gray-700 mb-1">No matches yet</p>
            <p className="text-sm text-gray-500">{myCareers.length === 0 ? "Add your career interests in your profile to find matches" : "As more youth join, you'll see matches here"}</p>
          </Card>
        ) : (
          <>
            <p className="text-xs text-gray-500 font-medium">{matches.length} people share your career interests</p>
            {matches.map((match, idx) => {
              const colors = getMatchColor(match.match_score);
              return (
                <motion.div key={match.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                  <Card
                    className="rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg transition-all"
                    onClick={() => setSelectedPerson(match)}
                    data-testid={`match-card-${match.id}`}
                  >
                    <div className={`h-1 bg-gradient-to-r ${colors.bg}`} />
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="w-12 h-12 ring-2 ring-gray-100">
                          {match.profile_picture ? <AvatarImage src={match.profile_picture} alt={match.name} /> : null}
                          <AvatarFallback className="bg-gray-100 text-lg">{match.avatar_emoji || match.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-bold text-sm truncate">{match.name}</p>
                            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${colors.light}`}>
                              <Sparkles className={`w-3 h-3 ${colors.text}`} />
                              <span className={`text-xs font-bold ${colors.text}`}>{match.match_score}%</span>
                            </div>
                          </div>
                          {match.school && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <GraduationCap className="w-3 h-3" /> {match.school}
                            </p>
                          )}
                          {match.shared_careers.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {match.shared_careers.map((career, i) => (
                                <Badge key={i} className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200">
                                  <Briefcase className="w-2.5 h-2.5 mr-1" />{career}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </>
        )}
      </div>

      {/* Profile Detail Popup */}
      <AnimatePresence>
        {selectedPerson && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={() => setSelectedPerson(null)}>
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              className="bg-white rounded-t-3xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-5">
                <div className="w-10 h-1.5 bg-gray-200 rounded-full mx-auto mb-4" />
                <button onClick={() => setSelectedPerson(null)} className="absolute top-4 right-4 p-1 rounded-full bg-gray-100 hover:bg-gray-200">
                  <X className="w-4 h-4 text-gray-500" />
                </button>

                {/* Profile Header */}
                <div className="flex items-center gap-4 mb-5">
                  <Avatar className="w-16 h-16 ring-2 ring-violet-100">
                    {selectedPerson.profile_picture ? <AvatarImage src={selectedPerson.profile_picture} alt={selectedPerson.name} /> : null}
                    <AvatarFallback className="bg-violet-50 text-2xl">{selectedPerson.avatar_emoji || selectedPerson.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-bold text-lg">{selectedPerson.name}</h3>
                    {selectedPerson.school && (
                      <p className="text-sm text-gray-500 flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {selectedPerson.school}</p>
                    )}
                    {selectedPerson.work_site && (
                      <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {selectedPerson.work_site}</p>
                    )}
                  </div>
                </div>

                {/* Match Score */}
                <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 ${getMatchColor(selectedPerson.match_score).light}`}>
                  <Sparkles className={`w-5 h-5 ${getMatchColor(selectedPerson.match_score).text}`} />
                  <span className={`font-bold ${getMatchColor(selectedPerson.match_score).text}`}>{selectedPerson.match_score}% Match</span>
                  <span className="text-xs text-gray-500 ml-1">based on career interests</span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-orange-50 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-orange-600">{selectedPerson.total_points}</p>
                    <p className="text-[10px] text-gray-500">Points</p>
                  </div>
                  <div className="bg-violet-50 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-violet-600">{selectedPerson.program_type === 'nextgen' ? 'NextGEN' : selectedPerson.program_type === 'upnext' ? 'UpNEXT' : selectedPerson.program_type || '—'}</p>
                    <p className="text-[10px] text-gray-500">Program</p>
                  </div>
                </div>

                {/* All Career Interests */}
                {selectedPerson.career_industries?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-bold text-gray-500 uppercase mb-2">Career Interests</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedPerson.career_industries.map((c, i) => {
                        const isShared = selectedPerson.shared_careers?.some(sc => sc.toLowerCase() === c.toLowerCase());
                        return (
                          <Badge key={i} className={`text-xs ${isShared ? 'bg-violet-100 text-violet-700 border border-violet-300' : 'bg-gray-100 text-gray-600'}`}>
                            {isShared && <Star className="w-2.5 h-2.5 mr-1" fill="currentColor" />}
                            {c}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Social Media */}
                {(selectedPerson.social_tiktok || selectedPerson.social_instagram || selectedPerson.social_twitter || selectedPerson.social_linkedin) && (
                  <div className="mb-4">
                    <p className="text-xs font-bold text-gray-500 uppercase mb-2">Socials</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedPerson.social_tiktok && (
                        <a href={`https://tiktok.com/@${selectedPerson.social_tiktok}`} target="_blank" rel="noreferrer"
                          className="px-3 py-1.5 rounded-full bg-gray-100 text-sm hover:bg-gray-200 transition-colors font-medium">
                          TikTok: @{selectedPerson.social_tiktok}
                        </a>
                      )}
                      {selectedPerson.social_instagram && (
                        <a href={`https://instagram.com/${selectedPerson.social_instagram}`} target="_blank" rel="noreferrer"
                          className="px-3 py-1.5 rounded-full bg-pink-50 text-sm hover:bg-pink-100 transition-colors text-pink-700 font-medium">
                          IG: @{selectedPerson.social_instagram}
                        </a>
                      )}
                      {selectedPerson.social_twitter && (
                        <a href={`https://x.com/${selectedPerson.social_twitter}`} target="_blank" rel="noreferrer"
                          className="px-3 py-1.5 rounded-full bg-blue-50 text-sm hover:bg-blue-100 transition-colors text-blue-700 font-medium">
                          X: @{selectedPerson.social_twitter}
                        </a>
                      )}
                      {selectedPerson.social_linkedin && (
                        <a href={`https://linkedin.com/in/${selectedPerson.social_linkedin}`} target="_blank" rel="noreferrer"
                          className="px-3 py-1.5 rounded-full bg-blue-50 text-sm hover:bg-blue-100 transition-colors text-blue-800 font-medium">
                          LinkedIn: {selectedPerson.social_linkedin}
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Shared Interests */}
                {selectedPerson.shared_interests?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-bold text-gray-500 uppercase mb-2">Shared Interests</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedPerson.shared_interests.map((i, idx) => (
                        <Badge key={idx} className="bg-pink-50 text-pink-700 text-xs"><Heart className="w-2.5 h-2.5 mr-1" fill="currentColor" />{i}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Navigation role="youth" />
    </div>
  );
};

export default PeopleLikeYouPage;
